import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCirclePlus, faHome } from "@fortawesome/free-solid-svg-icons";

import { Avatar, Breadcrumbs, Pagination, Stack } from "@mui/material";
import axios from "axios";
import TransparentButton from "common/components/Button/TransparentButton";
import CustomBreadcrumb from "common/components/CustomBreadcrumb";
import LoadingSpinner from "common/components/LoadingSpinner";
import { useAuth } from "common/hooks/useAuth";
import { BACKEND_URL_CONFIG } from "config";
import { ERRORS } from "errors";
import MainLayout from "layouts/MainLayout";
import { Fragment, useEffect, useMemo, useState } from "react";

import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { Dropdown, Menu, MenuButton, MenuItem } from "@mui/joy";
import ItemNotFoundContainer from "common/containers/ItemNotFoundContainer";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./MasksManagementPage.module.css";
export default function MasksManagementPage() {
  // States
  const [loading, setLoading] = useState(false);
  const [allMasks, setAllMasks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Contexts
  const navigate = useNavigate();
  const { currentUser, isUserAdmin, isUserManager } = useAuth();

  // Constants
  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(allMasks?.length / itemsPerPage);
  const filteredMasks = useMemo(
    () => allMasks.slice(indexOfFirstItem, indexOfLastItem),
    [allMasks, indexOfFirstItem, indexOfLastItem]
  );

  useEffect(() => {
    fetchMasks();
  }, []);

  const fetchMasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(BACKEND_URL_CONFIG.getMasksFilesList, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response.status === 200) {
        setAllMasks(response.data);
      }
    } catch (error) {
      if (error.response.status === 403) {
        toast.error(ERRORS.error_129);
        return;
      }
      toast.error(ERRORS.error_155);
    } finally {
      setLoading(false);
    }
  };

  const deleteMaskFile = async (maskFileId) => {
    setLoading(true);
    const deleteMaskFileEndpoint = BACKEND_URL_CONFIG.deleteMasksFile.replace(
      "{maskFileId}",
      maskFileId
    );
    try {
      const response = await axios.delete(deleteMaskFileEndpoint, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response.status === 200) {
        fetchMasks();
        toast.success("Le fichier a été supprimé");
        return;
      }
    } catch (error) {
      if (error.response.status === 403) {
        toast.error(ERRORS.error_129);
      }
      toast.error(ERRORS.error_156);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <MainLayout>
        <div className={styles.container}>
          <div className={styles["breadcrumbs-wrapper"]}>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <CustomBreadcrumb
                component="a"
                href="/"
                label="Page d'accueil"
                icon={<FontAwesomeIcon icon={faHome} />}
              />
              <CustomBreadcrumb component="a" href="#" label="Masques" />
            </Breadcrumbs>
          </div>
          <div className={styles["title-wrapper"]}>
            <div className={styles.title}>
              <span>Masques existantes</span>
              {loading && <LoadingSpinner />}
            </div>
            <div className={styles["actions-buttons-wrapper"]}>
              <TransparentButton onClick={() => navigate("/masks/add")}>
                <FontAwesomeIcon icon={faCirclePlus} />
                Nouveau
              </TransparentButton>
            </div>
          </div>
          <div className={styles["main-section"]}>
            {allMasks.length === 0 ? (
              <ItemNotFoundContainer message="Aucun fichier de masques n’a été trouvé. Vous pouvez en ajouter un !" />
            ) : (
              <div className={styles["table-section-wrapper"]}>
                <table id={styles["files-table"]}>
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Créé le</th>
                      <th>Créé par</th>
                      <th>Certifié</th>
                      <th>Versions</th>
                      <th>Projets</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMasks?.map((maskFile) => (
                      <tr className={styles.row} key={maskFile.id}>
                        <td
                          style={{
                            fontWeight: "bold",
                            textDecoration: "underline",
                          }}
                        >
                          {maskFile.name}
                        </td>
                        <td>
                          {new Date(maskFile.created_at * 1000).toLocaleString(
                            "fr-FR",
                            {
                              timeZone: "Europe/Paris",
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            }
                          )}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                            }}
                          >
                            {maskFile.created_by_details.length > 0 &&
                              maskFile.created_by_details.map((user, index) =>
                                user.photo_url ? (
                                  <Avatar
                                    key={index}
                                    alt={user.initiales}
                                    src={user.photo_url}
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      fontSize: 13,
                                      color: "#000",
                                      backgroundColor: "#cecece",
                                    }}
                                  />
                                ) : (
                                  <Avatar
                                    key={index}
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      fontSize: 13,
                                      color: "#000",
                                      backgroundColor: "#cecece",
                                    }}
                                  >
                                    {user.initiales}
                                  </Avatar>
                                )
                              )}
                          </div>
                        </td>
                        <td>
                          {maskFile.is_certified ? (
                            <FontAwesomeIcon
                              icon={faCircleCheck}
                              style={{ color: "#2b652b", fontSize: "20px" }}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              justifyContent: "center",
                              overflow: "overlay",
                              maxWidth: "250px",
                              maxHeight: "80px",
                            }}
                          >
                            {maskFile.used_in_versions_information.length >
                            0 ? (
                              maskFile.used_in_versions_information?.map(
                                (version) => (
                                  <span
                                    title={version.name}
                                    key={version.id}
                                    className={styles["tag-wrapper"]}
                                    style={{
                                      background: "#aa5f8c",
                                    }}
                                    onClick={() =>
                                      navigate(
                                        `/projects/${version.project_id}/version/${version.id}/run/load`
                                      )
                                    }
                                  >
                                    {version.name.substring(0, 10)}...
                                  </span>
                                )
                              )
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              justifyContent: "center",
                              overflow: "overlay",
                              maxWidth: "250px",
                            }}
                          >
                            {maskFile.used_in_projects_information.length >
                            0 ? (
                              maskFile.used_in_projects_information?.map(
                                (project) => (
                                  <span
                                    title={project.name}
                                    key={project.id}
                                    className={styles["tag-wrapper"]}
                                    style={{
                                      background: "#718bc5",
                                    }}
                                    onClick={() =>
                                      navigate(`/projects/${project.id}`)
                                    }
                                  >
                                    {project.name.substring(0, 10)}...
                                  </span>
                                )
                              )
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <Dropdown>
                            <MenuButton
                              style={{ padding: "11px 16px", border: "unset" }}
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faEllipsisVertical} />
                            </MenuButton>
                            <Menu>
                              <MenuItem
                                disabled={!isUserAdmin && !isUserManager}
                                onClick={() =>
                                  navigate(`/masks/edit/${maskFile.id}`)
                                }
                              >
                                Modifier
                              </MenuItem>
                              <MenuItem
                                disabled={!isUserAdmin && !isUserManager}
                                onClick={() => deleteMaskFile(maskFile.id)}
                              >
                                Supprimer
                              </MenuItem>
                            </Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {filteredMasks.length > 0 && (
              <div className={styles["pagination-container"]}>
                <Stack spacing={2}>
                  <Pagination
                    page={currentPage}
                    onChange={(e, value) => setCurrentPage(value)}
                    count={totalPages}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>
      </MainLayout>
    </Fragment>
  );
}
